import { i18n } from '@n8n/i18n';
import type { FrontendModuleDescription } from '@n8n/frontend-module-sdk';
import { VIEWS } from '@/app/constants';
import { INSTANCE_AI_MODALS } from './modals';
import {
	INSTANCE_AI_VIEW,
	INSTANCE_AI_THREAD_VIEW,
	INSTANCE_AI_SETTINGS_VIEW,
	INSTANCE_AI_NEW_VIEW,
} from './constants';
import {
	ensurePersonalProjectId,
	provisionLaunchedThread,
} from './composables/useInstanceAiHandoff';
// Experiment cleanup: remove with openWorkflowInAssistant.
import { launchWorkflowThread } from '@/experiments/openWorkflowInAssistant/launchWorkflowThread';
import {
	useInstanceAiAvailable,
	useInstanceAiReady,
} from './composables/useInstanceAiAvailability';
import { hasPermission } from '@/app/utils/rbac/permissions';

const InstanceAiView = async () => await import('./InstanceAiView.vue');
const InstanceAiEmptyView = async () => await import('./InstanceAiEmptyView.vue');
const InstanceAiThreadView = async () => await import('./InstanceAiThreadView.vue');
const SettingsInstanceAiView = async () => await import('./views/SettingsInstanceAiView.vue');

export const InstanceAiModule: FrontendModuleDescription = {
	id: 'instance-ai',
	name: 'AI Assistant',
	description: 'Chat with your n8n instance.',
	icon: 'sparkles',
	routes: [
		{
			path: '/assistant',
			component: InstanceAiView,
			beforeEnter: () => (useInstanceAiAvailable().value ? true : { name: VIEWS.HOMEPAGE }),
			meta: {
				layout: 'instanceAi',
				middleware: ['authenticated', 'custom'],
			},
			children: [
				{
					name: INSTANCE_AI_NEW_VIEW,
					path: 'new',
					component: InstanceAiEmptyView,
					beforeEnter: async (to) => {
						// Experiment cleanup: remove with openWorkflowInAssistant.
						const workflowRedirect = await launchWorkflowThread(to.query);
						if (workflowRedirect) return workflowRedirect;

						// Numeric ids only, so a crafted URL can't inject prompt text.
						const raw = to.query.templateId;
						if (typeof raw !== 'string' || !/^\d+$/.test(raw)) {
							return { name: INSTANCE_AI_VIEW };
						}
						const templateId = raw;

						// Same canonical gate as the button and website beacon, so the guard
						// never refuses an entry point they advertise. Whoever can't use
						// the assistant — including an admin who hasn't finished setup, since
						// the launched thread sends its kickoff on arrival — still gets the
						// template.
						if (!useInstanceAiReady().value) {
							return { name: VIEWS.TEMPLATE_SETUP, params: { id: templateId } };
						}

						// Threads are project-bound; deep links launch into the personal project.
						const projectId = await ensurePersonalProjectId();
						if (!projectId) {
							return { name: INSTANCE_AI_VIEW };
						}

						// The thread view sends the stashed kickoff after it hydrates, so the
						// guard never races the runtime.
						const threadId = await provisionLaunchedThread(
							projectId,
							{
								message: i18n.baseText('instanceAi.launch.templateById.message', {
									interpolate: { id: templateId },
								}),
							},
							{ source: 'website-template', origin: 'external', sourceContext: { templateId } },
						);
						if (!threadId) {
							return { name: INSTANCE_AI_VIEW };
						}

						// Redirect with no query → URL cleared, back-button won't re-fire this.
						return { name: INSTANCE_AI_THREAD_VIEW, params: { threadId } };
					},
				},
				{
					name: INSTANCE_AI_VIEW,
					path: '',
					component: InstanceAiEmptyView,
				},
				{
					name: INSTANCE_AI_THREAD_VIEW,
					path: ':threadId',
					component: InstanceAiThreadView,
					props: true,
				},
			],
		},
		// Permanent redirects from the legacy `/instance-ai` path to `/assistant`.
		{
			path: '/instance-ai',
			redirect: (to) => ({ name: INSTANCE_AI_VIEW, query: to.query, hash: to.hash }),
		},
		{
			path: '/instance-ai/:threadId',
			redirect: (to) => ({
				name: INSTANCE_AI_THREAD_VIEW,
				params: { threadId: to.params.threadId },
				query: to.query,
				hash: to.hash,
			}),
		},
		{
			path: 'assistant',
			name: INSTANCE_AI_SETTINGS_VIEW,
			component: SettingsInstanceAiView,
			meta: {
				layout: 'settings',
				middleware: ['authenticated', 'rbac', 'custom'],
				middlewareOptions: {
					rbac: {
						scope: ['instanceAi:message', 'instanceAi:manage'],
					},
				},
				telemetry: {
					pageCategory: 'settings',
				},
			},
		},
		// Permanent redirect from the legacy `/settings/instance-ai` path.
		{
			path: 'instance-ai',
			redirect: (to) => ({ name: INSTANCE_AI_SETTINGS_VIEW, query: to.query, hash: to.hash }),
			meta: {
				telemetry: {
					pageCategory: 'settings',
				},
			},
		},
		// Permanent redirect from the removed `/settings/assistant/credentials` page.
		{
			path: 'assistant/credentials',
			redirect: (to) => ({ name: INSTANCE_AI_SETTINGS_VIEW, query: to.query, hash: to.hash }),
			meta: {
				telemetry: {
					pageCategory: 'settings',
				},
			},
		},
	],
	projectTabs: {
		overview: [],
		project: [],
	},
	resources: [],
	modals: INSTANCE_AI_MODALS,
	pushHandlers: {
		// Credits are instance-level state, so the module owns this push type through
		// the SDK registry instead of a view-scoped store listener. The store is
		// imported lazily so it stays out of the startup bundle.
		updateInstanceAiCredits: async (event) => {
			const { useInstanceAiStore } = await import('./instanceAi.store');
			useInstanceAiStore().handleCreditsPush(event.data);
		},
	},
	settingsPages: [
		{
			id: 'settings-instance-ai',
			icon: 'sparkles',
			label: i18n.baseText('settings.n8nAgent'),
			position: 'top',
			route: { to: { name: INSTANCE_AI_SETTINGS_VIEW } },
			preview: true,
			get available() {
				return hasPermission(['rbac'], {
					rbac: { scope: ['instanceAi:message', 'instanceAi:manage'] },
				});
			},
		},
	],
};
